import { createTheme } from '@mui/material';

declare module '@mui/material/styles' {
	interface Palette {
		white: Palette['secondary'];
		black: Palette['secondary'];
		navLink: Palette['secondary'];
		inputFocused: Palette['secondary'];
	}
	interface PaletteOptions {
		white?: PaletteOptions['secondary'];
		black?: PaletteOptions['secondary'];
		navLink?: PaletteOptions['secondary'];
		inputFocused?: PaletteOptions['secondary'];
	}

	interface SimplePaletteColorOptions {
		lighter?: string;
		lightest?: string;
		darker?: string;
		darkest?: string;
	}

	interface PaletteColor {
		lighter?: string;
		lightest?: string;
		darker?: string;
		darkest?: string;
	}
}

declare module '@mui/material/Button' {
	interface ButtonPropsColorOverrides {
		navLink: true;
	}
}

declare module '@mui/material/Button' {
	interface ButtonPropsColorOverrides {
		neutralButton: true;
	}
}

declare module '@mui/material/TextField' {
	interface TextFieldPropsColorOverrides {
		inputFocused: true;
	}
}

declare module '@mui/material/Chip' {
	interface ChipPropsColorOverrides {
		neutralWarning: true;
		neutralSuccess: true;
		neutralError: true;
		neutralPrimary: true;
		neutralSecondary: true;
		neutralInfo: true;
		neutral: true;
	}
}

declare module '@mui/material/styles' {
	interface Palette {
		neutral: Palette['primary'];
		neutralWarning: Palette['primary'];
		neutralSuccess: Palette['primary'];
		neutralError: Palette['primary'];
		neutralPrimary: Palette['primary'];
		neutralSecondary: Palette['primary'];
		neutralInfo: Palette['primary'];
	}
	interface PaletteOptions {
		neutral: PaletteOptions['primary'];
		neutralWarning: Palette['primary'];
		neutralSuccess: Palette['primary'];
		neutralError: Palette['primary'];
		neutralPrimary: Palette['primary'];
		neutralSecondary: Palette['primary'];
		neutralInfo: Palette['primary'];
	}
}

const primaryDarkest = '#0C1929';
const primaryDarker = '#152C47';
const primaryDark = '#1E3E66';
const primaryNormal = '#275184';
const primaryLight = '#3063A2';
const primaryLighter = '#548ACD';
const primaryLightest = '#9FBEE3';
const primaryOpacity15 = 'rgba(39, 81, 132, 0.15)';

const secondaryDarkest = '#092F25';
const secondaryDarker = '#105040';
const secondaryDark = '#198167';
const secondaryNormal = '#23B28E';
const secondaryLight = '#38D8B0';
const secondaryLighter = '#9AEBD7';
const secondaryLightest = '#CCF5EA';
const secondaryOpacity15 = 'rgba(35, 178, 142, 0.15)';

const whiteDarkest = '#C5CAD1';
const whiteDarker = '#D0D4DA';
const whiteDark = '#DBDEE2';
const whiteNormal = '#E6E8EB';
const whiteLight = '#F1F2F4';
const whiteLighter = '#FCFCFC';
const whiteLightest = '#FFFFFF';
const whiteOpacity40 = 'rgba(255, 255, 255, 0.4)';
const whiteOpacity60 = 'rgba(255, 255, 255, 0.6)';

const blackDarkest = '#0E0E0F';
const blackDarker = '#202224';
const blackDark = '#292C2E';
const blackNormal = '#3C3F43';
const blackLight = '#61666C';
const blackLighter = '#888D94';
const blackLightest = '#B1B5B9';
const blackOpacity15 = 'rgba(14, 14, 15, 0.15)';
const blackOpacity40 = 'rgba(14, 14, 15, 0.4)';
const blackOpacity60 = 'rgba(14, 14, 15, 0.6)';

const success = '#2E7D32';
const successOpacity15 = 'rgba(46, 125, 50, 0.15)';
const info = '#0288D1';
const infoOpacity15 = 'rgba(2, 136, 209, 0.15)';
const warning = '#ED6C02';
const warningOpacity15 = 'rgba(237, 108, 2, 0.15)';
const error = '#D32F2F';
const errorOpacity15 = 'rgba(211, 47, 47, 0.15)';
const errorDarkest = '#5F2120';

export type MUIColor =
	| 'primary'
	| 'secondary'
	| 'inherit'
	| 'info'
	| 'success'
	| 'warning'
	| 'error'
	| 'navLink';
export type MUIVariant = 'text' | 'outlined' | 'contained';
const defaultTheme = createTheme();
export default {
	primary: {
		darkest: primaryDarkest,
		darker: primaryDarker,
		dark: primaryDark,
		main: primaryNormal,
		light: primaryLight,
		lighter: primaryLighter,
		lightest: primaryLightest,
		contrastText: whiteLightest, // This value might change
		opacity15: primaryOpacity15,
	},
	secondary: {
		darkest: secondaryDarkest,
		darker: secondaryDarker,
		dark: secondaryDark,
		main: secondaryNormal,
		light: secondaryLight,
		lighter: secondaryLighter,
		lightest: secondaryLightest,
		contrastText: whiteLightest,
		opacity15: secondaryOpacity15,
	},
	white: {
		darkest: whiteDarkest,
		darker: whiteDarker,
		dark: whiteDark,
		main: whiteNormal,
		light: whiteLight,
		lighter: whiteLighter,
		lightest: whiteLightest,
		contrastText: whiteLightest,
		opacity40: whiteOpacity40,
		opacity60: whiteOpacity60,
	},
	black: {
		darkest: blackDarkest,
		darker: blackDarker,
		dark: blackDark,
		main: blackNormal,
		light: blackLight,
		lighter: blackLighter,
		lightest: blackLightest,
		contrastText: whiteLightest,
		opacity15: blackOpacity15,
		opacity40: blackOpacity40,
		opacity60: blackOpacity60,
	},
	success: {
		main: success,
		opacity15: successOpacity15,
	},
	info: {
		main: info,
		opacity15: infoOpacity15,
	},
	warning: {
		main: warning,
		opacity15: warningOpacity15,
	},
	error: {
		main: error,
		darkest: errorDarkest,
		opacity15: errorOpacity15,
	},
	background: {
		default: whiteLighter,
	},
	// custom colors
	navLink: {
		main: whiteLightest,
	},
	inputFocused: {
		main: secondaryLight,
	},
	neutralButton: {
		main: blackNormal,
	},
	neutral: defaultTheme.palette.augmentColor({
		color: {
			main: blackOpacity15,
			contrastText: blackNormal,
		},
	}),
	neutralPrimary: defaultTheme.palette.augmentColor({
		color: {
			main: primaryOpacity15,
			contrastText: primaryNormal,
		},
	}),
	neutralSecondary: defaultTheme.palette.augmentColor({
		color: {
			main: secondaryOpacity15,
			contrastText: secondaryNormal,
		},
	}),
	neutralInfo: defaultTheme.palette.augmentColor({
		color: {
			main: infoOpacity15,
			contrastText: info,
		},
	}),
	neutralSuccess: defaultTheme.palette.augmentColor({
		color: {
			main: successOpacity15,
			contrastText: success,
		},
	}),
	neutralWarning: defaultTheme.palette.augmentColor({
		color: {
			main: warningOpacity15,
			contrastText: warning,
		},
	}),
	neutralError: defaultTheme.palette.augmentColor({
		color: {
			main: errorOpacity15,
			contrastText: error,
		},
	}),
};
